import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import ConfirmModalMUI from '../warehouse/modals/ConfirmModalMUI';
import FeedbackModalMUI from '../warehouse/modals/FeedbackModalMUI';
import { handleCreateOrcamento } from '../services/api';

// Lista de países para dropdown
const countries = [
  { code: 'AL', label: 'Albânia – AL' },
  { code: 'AD', label: 'Andorra – AD' },
  { code: 'AM', label: 'Arménia – AM' },
  { code: 'AT', label: 'Áustria – AT' },
  { code: 'AZ', label: 'Azerbaijão – AZ' },
  { code: 'BY', label: 'Bielorrússia – BY' },
  { code: 'BE', label: 'Bélgica – BE' },
  { code: 'BA', label: 'Bósnia e Herzegovina – BA' },
  { code: 'BG', label: 'Bulgária – BG' },
  { code: 'HR', label: 'Croácia – HR' },
  { code: 'CY', label: 'Chipre – CY' },
  { code: 'CZ', label: 'República Tcheca – CZ' },
  { code: 'DK', label: 'Dinamarca – DK' },
  { code: 'EE', label: 'Estónia – EE' },
  { code: 'FI', label: 'Finlândia – FI' },
  { code: 'FR', label: 'França – FR' },
  { code: 'GE', label: 'Geórgia – GE' },
  { code: 'DE', label: 'Alemanha – DE' },
  { code: 'GR', label: 'Grécia – GR' },
  { code: 'HU', label: 'Hungria – HU' },
  { code: 'IS', label: 'Islândia – IS' },
  { code: 'IE', label: 'Irlanda – IE' },
  { code: 'IT', label: 'Itália – IT' },
  { code: 'LV', label: 'Letónia – LV' },
  { code: 'LI', label: 'Liechtenstein – LI' },
  { code: 'LT', label: 'Lituânia – LT' },
  { code: 'LU', label: 'Luxemburgo – LU' },
  { code: 'MT', label: 'Malta – MT' },
  { code: 'MD', label: 'Moldávia – MD' },
  { code: 'MC', label: 'Mónaco – MC' },
  { code: 'ME', label: 'Montenegro – ME' },
  { code: 'NL', label: 'Países Baixos (Holanda) – NL' },
  { code: 'MK', label: 'Macedónia do Norte – MK' },
  { code: 'NO', label: 'Noruega – NO' },
  { code: 'PL', label: 'Polónia – PL' },
  { code: 'PT', label: 'Portugal – PT' },
  { code: 'RO', label: 'Roménia – RO' },
  { code: 'RU', label: 'Rússia – RU' },
  { code: 'SM', label: 'San Marino – SM' },
  { code: 'RS', label: 'Sérvia – RS' },
  { code: 'SK', label: 'Eslováquia – SK' },
  { code: 'SI', label: 'Eslovénia – SI' },
  { code: 'ES', label: 'Espanha – ES' },
  { code: 'SE', label: 'Suécia – SE' },
  { code: 'CH', label: 'Suíça – CH' },
  { code: 'TR', label: 'Turquia – TR' },
  { code: 'UA', label: 'Ucrânia – UA' },
  { code: 'GB', label: 'Reino Unido – GB' },
  { code: 'VA', label: 'Cidade do Vaticano (Santa Sé) – VA' }
];

/**
 * Modal para criação de orçamentos.
 * Props:
 *  - open (bool)
 *  - onClose (func)
 *  - apiBaseURL (string) // não usado diretamente mas mantido para consistência
 */
const Orcamentos = ({ open, onClose, apiBaseURL }) => {
  const [date, setDate] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState(null);
  const [volumeCount, setVolumeCount] = useState(1);
  const [volumeDetails, setVolumeDetails] = useState([{ weight: 0, shipmentType: 'Palete Euro', height: 0 }]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  // Ajusta detalhes de volume
  useEffect(() => {
    setVolumeDetails(prev => {
      const clone = [...prev];
      if (clone.length < volumeCount) {
        return [...clone, ...Array(volumeCount - clone.length).fill({ weight: 0, shipmentType: 'Palete Euro', height: 0 })];
      }
      return clone.slice(0, volumeCount);
    });
  }, [volumeCount]);

  const handleVolumeDetailChange = (index, field, value) => {
    setVolumeDetails(prev => {
      const clone = [...prev];
      clone[index] = { ...clone[index], [field]: value };
      return clone;
    });
  };

  // Exibe modal de confirmação
  const handleConfirmClick = () => setConfirmOpen(true);

  // Dispara chamada ao backend e seta mensagem com totalAmount
  const handleConfirm = async () => {
    setConfirmOpen(false);
    setFeedbackOpen(true);
    setIsSubmitting(true);
    try {
      const payload = { date, postalCode, country: country?.code || '', volumeDetails };
      const res = await handleCreateOrcamento(payload);
      const amount = res.data.totalAmount;
      setFeedbackMessage(`Orçamento estimado: €${amount}`);
    } catch (e) {
      console.error(e);
      setFeedbackMessage('Erro ao criar orçamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmCancel = () => setConfirmOpen(false);
  const handleFeedbackClose = () => { setFeedbackOpen(false); setFeedbackMessage(''); onClose(); };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Criar Orçamento</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Data"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={date}
                onChange={e => setDate(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Código Postal"
                fullWidth
                value={postalCode}
                onChange={e => setPostalCode(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={countries}
                getOptionLabel={opt => opt.label}
                value={country}
                onChange={(e, v) => setCountry(v)}
                renderInput={params => <TextField {...params} label="País" required />}
                isOptionEqualToValue={(opt, val) => opt.code === val.code}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Número de Paletes"
                type="number"
                fullWidth
                value={volumeCount}
                onChange={e => setVolumeCount(Math.max(1, parseInt(e.target.value) || 1))}
                required
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3 }}>
            {volumeDetails.map((detail, idx) => (
              <Box key={idx} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1">Palete {idx + 1}</Typography>
                <TextField
                  label={`Peso Palete ${idx + 1}`}
                  type="number"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={detail.weight}
                  onChange={e => handleVolumeDetailChange(idx, 'weight', parseInt(e.target.value) || 0)}
                  required
                />
                <FormControl fullWidth sx={{ mt: 2 }} required>
                  <Select
                    value={detail.shipmentType}
                    onChange={e => handleVolumeDetailChange(idx, 'shipmentType', e.target.value)}
                  >
                    <MenuItem value="Palete Euro">Palete Euro</MenuItem>
                    <MenuItem value="Volume">Volume</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={`Altura Palete ${idx + 1}`}
                  type="number"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={detail.height}
                  onChange={e => handleVolumeDetailChange(idx, 'height', parseInt(e.target.value) || 0)}
                  required
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmClick}
            disabled={!date || !postalCode || !country}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModalMUI
        open={confirmOpen}
        message="Tem certeza que deseja criar este orçamento?"
        onConfirm={handleConfirm}
        onCancel={handleConfirmCancel}
      />

      <FeedbackModalMUI
        open={feedbackOpen}
        isSubmitting={isSubmitting}
        submissionComplete={!isSubmitting && !!feedbackMessage}
        error={feedbackMessage.startsWith('Erro') ? feedbackMessage : null}
        feedbackMessage={feedbackMessage}
        onClose={handleFeedbackClose}
      />
    </>
  );
};

export default Orcamentos;
